import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ChatService {
  private isConversationStarted = new BehaviorSubject<boolean>(false);
  isConversationStarted$ = this.isConversationStarted.asObservable();

  private messagesMap: { [chatId: number]: any[] } = {};
  private messageSubjects: { [chatId: number]: BehaviorSubject<any[]> } = {};

  // Add a BehaviorSubject to hold the active chat ID.
  private activeChatIdSubject = new BehaviorSubject<number | null>(null);
  activeChatId$ = this.activeChatIdSubject.asObservable();

  private currentChatId: number | undefined;
  private logoClickEvent = false;
  private _logoClickEvent = new BehaviorSubject<any>(this.logoClickEvent);
  eraseEvent = this._logoClickEvent.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  private chatUpdatedSubject = new BehaviorSubject<{ chatId: number, updatedAt?: string } | null>(null);
  chatUpdated$ = this.chatUpdatedSubject.asObservable();

  notifyChatUpdated(chatId: number, updatedAt?: string) {
    this.chatUpdatedSubject.next({ chatId, updatedAt });
  }

  // Method to update isLoading state
  setLoading(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  changeEraseEvent(value: boolean) {
    this._logoClickEvent.next(value);
  }

  changeStatus(isStarted: boolean) {
    this.isConversationStarted.next(isStarted);
  }

  clearChatId() {
    this.currentChatId = undefined;
    this.activeChatIdSubject.next(null); // update active chat ID observable
    this.messagesMap = {};
    this.messageSubjects = {};
  }

  setChatId(chatId: number) {
    this.currentChatId = chatId;
    this.activeChatIdSubject.next(chatId); // notify subscribers of the new active chat ID
    if (!this.messagesMap[chatId]) {
      this.messagesMap[chatId] = [];
    }
    if (!this.messageSubjects[chatId]) {
      this.messageSubjects[chatId] = new BehaviorSubject<any[]>(this.messagesMap[chatId] || []);
    }
  }

  getChatId(): number | undefined {
    return this.currentChatId;
  }

  addMessage(chatId: number, message: any) {
    if (!this.messagesMap[chatId]) {
      this.messagesMap[chatId] = [];
      this.messageSubjects[chatId] = new BehaviorSubject<any[]>(this.messagesMap[chatId] || []);
    }
    this.messagesMap[chatId].push(message);
    if (this.messageSubjects[chatId]) {
      this.messageSubjects[chatId].next(this.messagesMap[chatId]);
    }
  }

  patchMessage(
      chatId: number,
      messageId: number,
      changes: Partial<any>
  ) {
    const messages = this.messagesMap[chatId];
    if (!messages) { return; }

    const idx = messages.findIndex(m => m.id === messageId);
    if (idx === -1) { return; }

    messages[idx] = { ...messages[idx], ...changes };

    this.messageSubjects[chatId].next([...messages]);
  }

  // Now we simply accept an array of messages
  setMessages(chatId: number, messages: any[]) {
    if (!this.messagesMap[chatId]) {
      this.messagesMap[chatId] = [];
    }

    // Preserve UI state flags from existing messages when merging with fresh data
    const existingMessages = this.messagesMap[chatId];
    const uiStateFlags = ['rated', 'show_feedback_box', 'rating', 'show_thanks_message', 'feedback_text', 'show_other_modal'];

    this.messagesMap[chatId] = messages.map(newMessage => {
      // Find corresponding existing message by ID
      const existingMessage = existingMessages.find(existing => existing.id === newMessage.id);

      if (existingMessage) {
        // Preserve UI state flags from existing message
        const preservedFlags: any = {};
        uiStateFlags.forEach(flag => {
          if (existingMessage.hasOwnProperty(flag)) {
            preservedFlags[flag] = existingMessage[flag];
          }
        });

        return {...newMessage, ...preservedFlags};
      }

      return newMessage;
    });
    if (this.messageSubjects[chatId]) {
      this.messageSubjects[chatId].next(this.messagesMap[chatId]);
    }
  }

  getMessages(chatId: number): any[] {
    return this.messagesMap[chatId] || [];
  }

  getMessagesObservable(chatId: number): BehaviorSubject<any[]> {
    if (!this.messageSubjects[chatId]) {
      this.messageSubjects[chatId] = new BehaviorSubject<any[]>(this.messagesMap[chatId] || []);
    }
    return this.messageSubjects[chatId];
  }
}
